import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useModuleContext } from './contexts.tsx';

/**
 * Module manifest entry
 */
export interface ModuleManifest {
  id: string;
  name: string;
  version: string;
  baseRoute: string;
  description?: string;
  icon?: string;
  views?: ModuleView[];
}

/**
 * View within a module
 */
export interface ModuleView {
  id: string;
  name: string;
  path: string;
  icon?: string;
  component?: string;
}

/**
 * Module registry containing all loaded modules
 */
export interface ModuleRegistry {
  modules: Record<string, ModuleManifest>;
  getModule: (moduleId: string) => ModuleManifest | undefined;
  getView: (moduleId: string, viewId: string) => ModuleView | undefined;
}

/**
 * Hook to navigate to a view in another module
 * Constructs the full route path based on the module's base route
 *
 * @param moduleId - ID of the target module
 * @param viewId - ID of the view within the module
 * @returns Navigation function
 *
 * @example
 * const navigateToEquipment = useModuleAction('equipment', 'detail');
 * navigateToEquipment({ equipmentId: '123' });
 */
export const useModuleAction = (
  moduleId: string,
  viewId: string
): ((params?: Record<string, string>) => void) => {
  const navigate = useNavigate();
  const { registry } = useModuleContext();

  const navigateTo = useCallback(
    (params?: Record<string, string>) => {
      if (!registry) {
        console.error('Module registry not available');
        return;
      }

      const module = registry.getModule(moduleId);
      if (!module) {
        console.error(`Module not found: ${moduleId}`);
        return;
      }

      const view = registry.getView(moduleId, viewId);
      if (!view) {
        console.error(
          `View not found: ${viewId} in module ${moduleId}`
        );
        return;
      }

      // Build the route path
      let path = `${module.baseRoute}/${view.path}`;

      // Replace URL parameters
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          path = path.replace(`:${key}`, value);
        });
      }

      navigate(path);
    },
    [navigate, registry, moduleId, viewId]
  );

  return navigateTo;
};

/**
 * Hook to access the module registry
 * Provides metadata about all loaded modules and their views
 *
 * @returns Module registry object
 *
 * @example
 * const registry = useModuleRegistry();
 * const modules = Object.values(registry.modules);
 */
export const useModuleRegistry = (): ModuleRegistry | null => {
  const { registry } = useModuleContext();
  return registry || null;
};

/**
 * Hook to get a specific module's metadata
 *
 * @param moduleId - ID of the module
 * @returns Module manifest or undefined if not found
 *
 * @example
 * const equipmentModule = useGetModule('equipment');
 */
export const useGetModule = (moduleId: string): ModuleManifest | undefined => {
  const registry = useModuleRegistry();
  return registry?.getModule(moduleId);
};

/**
 * Hook to get a specific view's metadata
 *
 * @param moduleId - ID of the module
 * @param viewId - ID of the view
 * @returns View manifest or undefined if not found
 *
 * @example
 * const detailView = useGetView('equipment', 'detail');
 */
export const useGetView = (
  moduleId: string,
  viewId: string
): ModuleView | undefined => {
  const registry = useModuleRegistry();
  return registry?.getView(moduleId, viewId);
};

/**
 * Hook to list all available modules
 *
 * @returns Array of module manifests
 *
 * @example
 * const allModules = useModuleList();
 */
export const useModuleList = (): ModuleManifest[] => {
  const registry = useModuleRegistry();
  if (!registry) return [];
  return Object.values(registry.modules);
};
